import { Add, ExpandMoreOutlined } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GET, PATCH, POST } from "../../../Utilities/BaseService";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { UserRegions } from "../../User/UserRegions";

const AddFixedBill = ({ updateMode = false }) => {
    const [fixedBill, setFixedBill] = useState<any>({
        name: "",
        minimumBillAmount: "",
        status: "ACTIVE",
        connectionType: "",
        regions: [],
        users: [],
        meters: [],
    });
    const [feedback, setFeedback] = useState("");
    const [id, setId] = useState("");
    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        hamlet: false,
        powerstation: false,
    });
    const [changes, setChanges] = useState({
        district: 0,
        village: 0,
        hamlet: 0,
        powerstation: 0,
    });

    const theme = useTheme();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const duplicateId = params.get("duplicateId");
    const isDuplicate = Boolean(duplicateId);

    const { data: usersRes, isLoading: usersLoading } = useQuery(
        ["users"],
        () => GET("/user", { page: 1, limit: 10000 })
    );

    const users = usersRes?.data?.rows ?? [];

    const { data: priceTypeById } = useQuery(
        [`bill-rule-${id}`],
        () => GET("/reading/billing-rule", { id: id }),
        {
            enabled: Boolean(updateMode && id),

            onSuccess(res) {
                const _pt = res.data.rows[0];

                setFixedBill({
                    name: _pt.name,
                    minimumBillAmount: _pt.minimumBillAmount,
                    status: _pt.status,
                    regions: _pt.BillingRuleRegions.map(
                        (r: {
                            district: { id: number } | null;
                            village: { id: number } | null;
                            hamlet: { id: number } | null;
                            powerstation: { id: number } | null;
                        }) => ({
                            district: r.district?.id || null,
                            village: r.village?.id || null,
                            hamlet: r.hamlet?.id || null,
                            powerstation: r.powerstation?.id || null,
                        })
                    ),
                    users: _pt.billingRuleUsers.map((u: any) => u.user.id),
                    meters: _pt.billingRuleMeters?.map((m: any) => m.meter.id) ?? [],
                    connectionType: _pt.connectionType.id,
                });

                setChanges({
                    district: 1,
                    village: 1,
                    hamlet: 1,
                    powerstation: 1,
                });
            },
        }
    );

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!updateMode) {
            addMutation.mutate();
        } else if (isDuplicate) {
            addMutation.mutate();
        } else {
            updateMutation.mutate();
        }
    };

    const addMutation = useMutation(
        () =>
            POST(
                "/reading/billing-rule",
                Object.fromEntries(
                    Object.entries(fixedBill).map(([k, v]) => [
                        k,
                        v && v !== "all" ? v : null,
                    ])
                )
            ),
        {
            onSuccess(res) {
                setFeedback(res.data.message);
            },

            onError(err: any) {
                setFeedback(err.response.data.message);
            },
        }
    );
    const updateMutation = useMutation(
        () =>
            PATCH(
                "/reading/billing-rule",
                Object.fromEntries(
                    Object.entries(fixedBill).map(([k, v]) => [
                        k,
                        v && v !== "all" ? v : null,
                    ])
                ),
                { id }
            ),
        {
            onSuccess(res) {
                setFeedback(res.data.message);
            },

            onError(err: any) {
                setFeedback(err.response.data.message);
            },
        }
    );

    useEffect(() => {
        if (updateMode) {
            const params = new URLSearchParams(location.search);
            const _id = params.get("id");

            if (_id) {
                setId(_id);
            }
        }
    }, [updateMode]);

    return (
        <>
            <Card
                elevation={0}
                sx={{ bgcolor: theme.palette.common.white, mb: "1rem" }}
            >
                <CardHeader
                    title={
                        updateMode
                            ? "update fixed bill"
                            : isDuplicate
                              ? "Duplicate Fixed Type"
                              : "Add Fixed Bill"
                    }
                    sx={{
                        textTransform: "capitalize",
                        bgcolor: theme.palette.common.white,
                    }}
                />
                <CardContent>
                    <Divider sx={{ mb: 5 }} />

                    <Grid
                        container
                        component="form"
                        onSubmit={handleSubmit}
                        spacing={2}
                    >
                        <Grid item xs={12} md={6} lg={3}>
                            <TextField
                                size="small"
                                value={fixedBill.name}
                                label="Fixed Bill Name"
                                onChange={(ev) =>
                                    setFixedBill({
                                        ...fixedBill,
                                        name: ev.target.value,
                                    })
                                }
                                fullWidth
                                type="text"
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3}>
                            <TextField
                                size="small"
                                value={fixedBill.minimumBillAmount}
                                label="Minimum Bill Amount"
                                onChange={(ev) =>
                                    setFixedBill({
                                        ...fixedBill,
                                        minimumBillAmount:
                                            ev.target.value === ""
                                                ? null
                                                : (Number(
                                                      ev.target.value
                                                  ) as any),
                                    })
                                }
                                fullWidth
                                type="number"
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                label="Connection Type"
                                _name="connectionType"
                                api="/connection"
                                filter={fixedBill}
                                setFilter={setFixedBill}
                                required
                                defaultValue={
                                    updateMode || isDuplicate
                                        ? parseInt(fixedBill.connectionType)
                                        : undefined
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Status"
                                name="status"
                                value={fixedBill.status}
                                onChange={(ev: any) => {
                                    setFixedBill({
                                        ...fixedBill,
                                        status: ev.target.value,
                                    });
                                }}
                                select
                            >
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={users}
                                size="small"
                                getOptionLabel={(option) => option.name}
                                value={users.filter((u: any) =>
                                    fixedBill.users?.includes(u.id)
                                )}
                                onChange={(event, newValue) => {
                                    setFixedBill({
                                        ...fixedBill,
                                        users: newValue.map((u) => u.id), // store only IDs (same as before)
                                    });
                                }}
                                filterSelectedOptions
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Users"
                                        fullWidth
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        {option.name}
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.name}
                                            {...getTagProps({ index })}
                                            key={option.id}
                                        />
                                    ))
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            {(!updateMode || priceTypeById) && (
                                <UpdatedSearchableInput
                                    key={`meters-${id}-${fixedBill.meters?.join("-")}`}
                                    label="Meter No."
                                    _name="meters"
                                    api="/customer/meter"
                                    filter={fixedBill}
                                    setFilter={setFixedBill}
                                    multiple
                                    params={{
                                        connectionType:
                                            fixedBill.connectionType &&
                                            fixedBill.connectionType !== "all"
                                                ? fixedBill.connectionType
                                                : undefined,
                                        status: "active",
                                    }}
                                    dep={[fixedBill.connectionType]}
                                    optionsPreprocessor={(opt: any) => ({
                                        ...opt,
                                        name: opt.meterNo,
                                    })}
                                    defaultValue={
                                        updateMode || isDuplicate
                                            ? fixedBill.meters
                                            : undefined
                                    }
                                />
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Accordion
                                elevation={0}
                                sx={{ bgcolor: "transparent" }}
                                variant="outlined"
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreOutlined />}
                                >
                                    <Typography>Regions</Typography>
                                </AccordionSummary>

                                <Divider />

                                <AccordionDetails>
                                    <UserRegions
                                        user={fixedBill}
                                        setUser={setFixedBill}
                                        changes={changes}
                                        setChanges={setChanges}
                                        updateMode={updateMode}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                startIcon={<Add />}
                                endIcon={
                                    addMutation.isLoading ? (
                                        <CircularProgress
                                            color="secondary"
                                            size="1rem"
                                        />
                                    ) : (
                                        ""
                                    )
                                }
                                color="secondary"
                                variant="outlined"
                                size="small"
                                type="submit"
                            >
                                {updateMode
                                    ? "update"
                                    : isDuplicate
                                      ? "Duplicate"
                                      : "add"}
                                {""} {""}Fixed Bill
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {addMutation.isSuccess || updateMutation.isSuccess ? (
                <Alert severity="success">{feedback}.</Alert>
            ) : addMutation.isError || updateMutation.isError ? (
                <Alert severity="error">{feedback}.</Alert>
            ) : null}
        </>
    );
};

export default AddFixedBill;
